import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SchwabMarketDataCallback = () => {
  const [code, setCode] = useState('');
  const [session, setSession] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation();

  useEffect(() => {
    // Parse URL parameters
    const urlParams = new URLSearchParams(location.search);
    const codeParam = urlParams.get('code');
    const sessionParam = urlParams.get('session');
    
    if (codeParam) {
      setCode(codeParam);
      // Automatically exchange code for tokens when code is received
      exchangeCodeForTokens(codeParam);
    }
    if (sessionParam) {
      setSession(sessionParam);
    }
  }, [location]);

  // Extract authorization code from the full URL (like SchwabModel does)
  const extractAuthCode = (fullUrl) => {
    try {
      const codeIndex = fullUrl.indexOf('code=');
      const atIndex = fullUrl.indexOf('%40');
      if (codeIndex !== -1 && atIndex !== -1) {
        return fullUrl.substring(codeIndex + 5, atIndex) + '@';
      }
      return null;
    } catch (err) {
      console.error('Error extracting auth code:', err);
      return null;
    }
  };

  // Exchange authorization code for access token (following SchwabModel implementation)
  const exchangeCodeForTokens = async (authCode) => {
    setLoading(true);
    setError('');
    
    try {
      // Extract the authorization code properly (matching SchwabModel logic)
      const fullUrl = window.location.href;
      const extractedCode = extractAuthCode(fullUrl);
      
      if (!extractedCode) {
        throw new Error('Could not extract authorization code from URL');
      }

      // Check if environment variables are properly set
      if (!process.env.REACT_APP_SCHWAB_CLIENT_ID || !process.env.REACT_APP_SCHWAB_CLIENT_SECRET) {
        throw new Error('Schwab client credentials not found in environment variables. Please check REACT_APP_SCHWAB_CLIENT_ID and REACT_APP_SCHWAB_CLIENT_SECRET.');
      }

      // Create credentials string and encode to base64 (matching SchwabModel)
      const credentials = `${process.env.REACT_APP_SCHWAB_CLIENT_ID}:${process.env.REACT_APP_SCHWAB_CLIENT_SECRET}`;
      const base64Credentials = btoa(credentials);

      console.log('Client ID:', process.env.REACT_APP_SCHWAB_CLIENT_ID); // Debug log
      console.log('Extracted Code:', extractedCode); // Debug log
      console.log('Base64 Credentials length:', base64Credentials.length); // Debug log

      // Use the correct Schwab OAuth endpoint (v1, not trader/v1) 
      // Following the Reddit guide implementation exactly
      const tokenResponse = await fetch('https://api.schwabapi.com/v1/oauth/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Basic ${base64Credentials}`
        },
        body: new URLSearchParams({
          'grant_type': 'authorization_code',
          'code': extractedCode,
          'redirect_uri': 'https://coffeestain.co/schwab/marketdata/oauth'  // Use coffeestain.co URL instead of localhost
        })
      });

      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        throw new Error(`Token exchange failed: ${tokenResponse.status} - ${errorText}`);
      }

      const tokenData = await tokenResponse.json();
      setAccessToken(tokenData.access_token);
      setRefreshToken(tokenData.refresh_token);
      
      // Automatically fetch AMZN market data
      await fetchAMZNMarketData(tokenData.access_token);
      
    } catch (err) {
      setError(`Failed to exchange code for tokens: ${err.message}`);
      console.error('Token exchange error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch market data for AMZN (using correct endpoint structure from SchwabModel)
  const fetchAMZNMarketData = async (token) => {
    try {
      // Use the correct quotes URL format with proper encoding
      const response = await fetch('https://api.schwabapi.com/marketdata/v1/quotes?symbols=AMZN&fields=quote%2Creference', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Market data fetch failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      setMarketData(data);
      
    } catch (err) {
      setError(`Failed to fetch AMZN market data: ${err.message}`);
      console.error('Market data fetch error:', err);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus(''), 2000);
    } catch (err) {
      setCopyStatus('Failed to copy');
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  const containerStyle = {
    maxWidth: '800px',
    margin: '2rem auto',
    padding: '2rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  };

  const titleStyle = {
    fontSize: '1.5rem',
    marginBottom: '1.5rem',
    color: '#333',
    textAlign: 'center'
  };

  const subtitleStyle = {
    fontSize: '1rem',
    marginBottom: '1rem',
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic'
  };

  const codeBoxStyle = {
    backgroundColor: '#fff',
    border: '2px solid #e9ecef',
    borderRadius: '6px',
    padding: '1rem',
    marginBottom: '1rem',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
    wordBreak: 'break-all',
    minHeight: '60px',
    position: 'relative'
  };

  const buttonStyle = {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    marginTop: '0.5rem'
  };

  const buttonHoverStyle = {
    backgroundColor: '#218838'
  };

  const statusStyle = {
    marginLeft: '0.5rem',
    color: copyStatus === 'Copied!' ? '#28a745' : '#dc3545',
    fontSize: '0.8rem'
  };

  const infoBoxStyle = {
    backgroundColor: '#e7f3ff',
    border: '1px solid #b3d9ff',
    borderRadius: '6px',
    padding: '1rem',
    marginBottom: '1.5rem',
    fontSize: '0.9rem'
  };

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>Schwab Market Data API Callback</h1>
      <p style={subtitleStyle}>Authorization for Market Data API Access</p>
      
      <div style={infoBoxStyle}>
        <strong>Market Data API Features:</strong>
        <ul style={{ marginBottom: 0, paddingLeft: '1.5rem' }}>
          <li>Real-time stock quotes</li>
          <li>Historical price data</li>
          <li>Options chains</li>
          <li>Market hours information</li>
        </ul>
      </div>
      
      {code ? (
        <>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Market Data Authorization Code:
            </label>
            <div style={codeBoxStyle}>
              {code}
            </div>
            <button
              style={buttonStyle}
              onClick={() => copyToClipboard(code)}
              onMouseOver={(e) => e.target.style.backgroundColor = buttonHoverStyle.backgroundColor}
              onMouseOut={(e) => e.target.style.backgroundColor = buttonStyle.backgroundColor}
            >
              Copy Code
            </button>
            {copyStatus && <span style={statusStyle}>{copyStatus}</span>}
          </div>

          {loading && (
            <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#e3f2fd', border: '1px solid #1976d2', borderRadius: '6px', textAlign: 'center' }}>
              <strong>🔄 Processing OAuth Flow...</strong>
              <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>
                Exchanging authorization code for access token and fetching AMZN market data...
              </p>
            </div>
          )}

          {error && (
            <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#ffebee', border: '1px solid #f44336', borderRadius: '6px' }}>
              <strong style={{ color: '#d32f2f' }}>❌ Error:</strong>
              <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>{error}</p>
            </div>
          )}

          {accessToken && (
            <div style={{ marginTop: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                ✅ Access Token (First 50 chars):
              </label>
              <div style={codeBoxStyle}>
                {accessToken.substring(0, 50)}...
              </div>
            </div>
          )}

          {marketData && (
            <div style={{ marginTop: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                📈 AMZN Market Data:
              </label>
              <div style={{ ...codeBoxStyle, backgroundColor: '#f0f8ff', border: '2px solid #4caf50' }}>
                {marketData.quote ? (
                  <div>
                    <div><strong>Symbol:</strong> {marketData.quote.symbol || 'AMZN'}</div>
                    <div><strong>Description:</strong> {marketData.reference?.description || marketData.quote?.description || 'Amazon.com Inc'}</div>
                    <div><strong>Last Price:</strong> ${marketData.quote.lastPrice}</div>
                    <div><strong>Change:</strong> ${marketData.quote.netChange} ({marketData.quote.netPercentChangeInDouble?.toFixed(2)}%)</div>
                    <div><strong>Volume:</strong> {marketData.quote.totalVolume?.toLocaleString()}</div>
                    <div><strong>Bid:</strong> ${marketData.quote.bidPrice} x {marketData.quote.bidSize}</div>
                    <div><strong>Ask:</strong> ${marketData.quote.askPrice} x {marketData.quote.askSize}</div>
                    <div><strong>High:</strong> ${marketData.quote.highPrice}</div>
                    <div><strong>Low:</strong> ${marketData.quote.lowPrice}</div>
                    <div><strong>Open:</strong> ${marketData.quote.openPrice}</div>
                    <div><strong>Previous Close:</strong> ${marketData.quote.closePrice}</div>
                    {marketData.reference?.marketCap && (
                      <div><strong>Market Cap:</strong> ${(marketData.reference.marketCap / 1000000000).toFixed(2)}B</div>
                    )}
                  </div>
                ) : marketData.AMZN ? (
                  <div>
                    <div><strong>Symbol:</strong> {marketData.AMZN.symbol}</div>
                    <div><strong>Description:</strong> {marketData.AMZN.description}</div>
                    <div><strong>Last Price:</strong> ${marketData.AMZN.lastPrice}</div>
                    <div><strong>Change:</strong> ${marketData.AMZN.netChange} ({marketData.AMZN.netPercentChangeInDouble?.toFixed(2)}%)</div>
                    <div><strong>Volume:</strong> {marketData.AMZN.totalVolume?.toLocaleString()}</div>
                    <div><strong>Bid:</strong> ${marketData.AMZN.bidPrice} x {marketData.AMZN.bidSize}</div>
                    <div><strong>Ask:</strong> ${marketData.AMZN.askPrice} x {marketData.AMZN.askSize}</div>
                    <div><strong>High:</strong> ${marketData.AMZN.highPrice}</div>
                    <div><strong>Low:</strong> ${marketData.AMZN.lowPrice}</div>
                    <div><strong>Open:</strong> ${marketData.AMZN.openPrice}</div>
                    <div><strong>Previous Close:</strong> ${marketData.AMZN.closePrice}</div>
                    <div><strong>Market Cap:</strong> ${(marketData.AMZN.marketCap / 1000000000).toFixed(2)}B</div>
                  </div>
                ) : (
                  <pre>{JSON.stringify(marketData, null, 2)}</pre>
                )}
              </div>
              <button
                style={buttonStyle}
                onClick={() => copyToClipboard(JSON.stringify(marketData, null, 2))}
                onMouseOver={(e) => e.target.style.backgroundColor = buttonHoverStyle.backgroundColor}
                onMouseOut={(e) => e.target.style.backgroundColor = buttonStyle.backgroundColor}
              >
                Copy Market Data JSON
              </button>
            </div>
          )}
          
          {session && (
            <div style={{ marginTop: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Session ID:
              </label>
              <div style={codeBoxStyle}>
                {session}
              </div>
              <button
                style={buttonStyle}
                onClick={() => copyToClipboard(session)}
                onMouseOver={(e) => e.target.style.backgroundColor = buttonHoverStyle.backgroundColor}
                onMouseOut={(e) => e.target.style.backgroundColor = buttonStyle.backgroundColor}
              >
                Copy Session
              </button>
            </div>
          )}

          {!loading && !error && marketData && (
            <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#e8f5e8', border: '1px solid #4caf50', borderRadius: '6px' }}>
              <strong>✅ OAuth Flow Completed Successfully!</strong>
              <ul style={{ marginBottom: 0, paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
                <li>Authorization code exchanged for access token</li>
                <li>AMZN market data retrieved successfully</li>
                <li>Access token expires in 30 minutes</li>
                <li>Use refresh token for continued access</li>
              </ul>
            </div>
          )}

          {!marketData && !loading && !error && accessToken && (
            <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '6px' }}>
              <strong>Next Steps:</strong>
              <ol style={{ marginBottom: 0, paddingLeft: '1.5rem' }}>
                <li>✅ Authorization code exchanged for access token</li>
                <li>🔄 Fetching AMZN market data...</li>
                <li>Store the refresh token securely for ongoing access</li>
              </ol>
            </div>
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', color: '#666' }}>
          <p>No authorization code found in the URL parameters.</p>
          <p>This page is designed to handle Schwab Market Data OAuth callbacks.</p>
        </div>
      )}
    </div>
  );
};

export default SchwabMarketDataCallback;
